import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { searchMovies, searchNews } from "@/utils/content";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category", label: "影视分类", end: false },
  { to: "/news", label: "资讯动态", end: false },
  { to: "/special", label: "专题合集", end: false },
  { to: "/about", label: "关于", end: false },
];

const hotKeywords = ["家庭伦理", "照护", "沉默", "白瓷之隙", "叙事"];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setSearchOpen(false);
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const shouldLock = searchOpen || menuOpen;
    document.body.style.overflow = shouldLock ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [searchOpen, menuOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSearchOpen(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const movieResults = useMemo(() => searchMovies(keyword).slice(0, 4), [keyword]);
  const newsResults = useMemo(() => searchNews(keyword).slice(0, 4), [keyword]);
  const totalResults = useMemo(
    () => searchMovies(keyword).length + searchNews(keyword).length,
    [keyword],
  );

  const submitSearch = () => {
    const q = keyword.trim();
    setSearchOpen(false);
    navigate(q ? `/category?q=${encodeURIComponent(q)}` : "/category");
  };

  const headerTone =
    isHome && !scrolled
      ? "bg-transparent border-transparent"
      : "bg-background-950/90 backdrop-blur-md border-background-800";

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 border-b transition-colors duration-300 ${headerTone}`}
      >
        <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-20 md:px-8">
          <Link to="/" className="group flex shrink-0 items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 transition-colors group-hover:bg-accent-400">
              <i className="ri-film-line text-lg text-background-950" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-base font-semibold tracking-[0.18em] text-foreground-950">
                日本伦理
              </span>
              <span className="mt-1 font-label text-[10px] uppercase tracking-[0.28em] text-foreground-500">
                Ethics Archive
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative px-3 py-2 text-sm transition-colors lg:px-4 ${
                    isActive
                      ? "text-foreground-950"
                      : "text-foreground-500 hover:text-foreground-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    {item.label}
                    {isActive && (
                      <span className="h-1 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-10 items-center gap-2 rounded-md border border-background-700 px-3 text-sm text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-400"
              aria-label="搜索影视作品与资讯"
            >
              <i className="ri-search-line text-base" aria-hidden="true"></i>
              <span className="hidden lg:inline">检索档案</span>
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400 md:hidden"
              aria-label="打开导航菜单"
            >
              <i className="ri-menu-line text-lg" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </header>

      {searchOpen && (
        <div className="fixed inset-0 z-50 animate-soft-in">
          <div
            className="absolute inset-0 bg-background-950/85 backdrop-blur-sm"
            onClick={() => setSearchOpen(false)}
          ></div>
          <div className="relative flex h-full w-full flex-col border-background-800 bg-background-900 md:mx-auto md:mt-24 md:h-auto md:max-h-[72vh] md:max-w-3xl md:rounded-lg md:border">
            <div className="flex items-center gap-3 border-b border-background-800 px-4 py-4 md:px-6">
              <i className="ri-search-line text-lg text-accent-400" aria-hidden="true"></i>
              <input
                autoFocus
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") submitSearch();
                }}
                type="text"
                name="site_search"
                placeholder="输入片名、导演、题材或关键词"
                className="h-9 w-full bg-transparent text-base text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-800 hover:text-foreground-900"
                aria-label="关闭搜索"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-5 md:px-6">
              {!keyword.trim() && (
                <div>
                  <p className="font-label text-xs uppercase tracking-[0.22em] text-foreground-500">
                    热门检索
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hotKeywords.map((word) => (
                      <button
                        key={word}
                        type="button"
                        onClick={() => setKeyword(word)}
                        className="rounded-full border border-background-700 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-400"
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                  <p className="mt-6 text-sm leading-relaxed text-foreground-500">
                    搜索范围包含全部 20 部收录作品与 10 篇行业资讯，支持片名、导演、演员与标签的模糊匹配。
                  </p>
                </div>
              )}

              {keyword.trim() && totalResults === 0 && (
                <div className="py-10 text-center">
                  <i className="ri-file-search-line text-3xl text-foreground-500" aria-hidden="true"></i>
                  <p className="mt-3 text-sm text-foreground-700">
                    没有找到与「{keyword.trim()}」相关的内容
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">
                    试试更短的关键词，例如「家庭」「照护」「叙事」
                  </p>
                </div>
              )}

              {movieResults.length > 0 && (
                <div className="mb-5">
                  <p className="font-label text-xs uppercase tracking-[0.22em] text-foreground-500">
                    影视作品
                  </p>
                  <ul className="mt-3 space-y-1">
                    {movieResults.map((movie) => (
                      <li key={movie.id}>
                        <Link
                          to={`/detail/${movie.id}`}
                          className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-800"
                        >
                          <span className="h-14 w-10 shrink-0 overflow-hidden rounded bg-background-800">
                            <img
                              src={movie.poster}
                              alt={`${movie.title}封面`}
                              title={`${movie.title} - 日本伦理影视作品`}
                              className="h-full w-full object-cover object-top"
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm text-foreground-900">
                              {movie.title}
                            </span>
                            <span className="mt-0.5 block truncate text-xs text-foreground-500">
                              {movie.year} · {movie.genre} · 导演 {movie.director}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {newsResults.length > 0 && (
                <div>
                  <p className="font-label text-xs uppercase tracking-[0.22em] text-foreground-500">
                    资讯解析
                  </p>
                  <ul className="mt-3 space-y-1">
                    {newsResults.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/news/${item.id}`}
                          className="flex items-start gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-800"
                        >
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded bg-background-800 text-accent-400">
                            <i className="ri-article-line text-sm" aria-hidden="true"></i>
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm leading-snug text-foreground-900">
                              {item.title}
                            </span>
                            <span className="mt-0.5 block text-xs text-foreground-500">
                              {item.category} · {item.date}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {keyword.trim() && totalResults > 0 && (
                <button
                  type="button"
                  onClick={submitSearch}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm text-foreground-950 transition-colors hover:bg-primary-600"
                >
                  查看全部结果（{totalResults}）
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-background-950/80 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 flex h-full w-[82%] max-w-xs flex-col border-l border-background-800 bg-background-900">
            <div className="flex items-center justify-between border-b border-background-800 px-5 py-5">
              <span className="font-label text-xs uppercase tracking-[0.26em] text-foreground-500">
                导航目录
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-700 text-foreground-600"
                aria-label="关闭导航菜单"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4">
              {navItems.map((item, index) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-md px-3 py-3.5 text-base transition-colors ${
                      isActive
                        ? "bg-background-800 text-foreground-950"
                        : "text-foreground-600"
                    }`
                  }
                >
                  <span className="flex items-center gap-3">
                    <span className="font-label text-xs text-accent-500">
                      0{index + 1}
                    </span>
                    {item.label}
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-500" aria-hidden="true"></i>
                </NavLink>
              ))}
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setSearchOpen(true);
                }}
                className="mt-4 flex w-full items-center gap-3 rounded-md bg-primary-500 px-3 py-3 text-sm text-foreground-950"
              >
                <i className="ri-search-line" aria-hidden="true"></i>
                搜索作品与资讯
              </button>
            </nav>
            <div className="border-t border-background-800 px-5 py-5">
              <p className="text-xs leading-relaxed text-foreground-500">
                本站为虚构影视资料演示站点，不提供播放与下载。
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}